import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, Minus } from 'lucide-react';
export function DifferentiationSection() {
  const options = [{
    name: 'Internal HR',
    purpose: 'Compliance & policy enforcement',
    independence: 'low',
    confidentiality: 'limited',
    timing: 'Often engaged when issues escalate',
    bestFor: 'Organisational risk management',
    isRecommended: false
  }, {
    name: 'EAP',
    purpose: 'Personal psychological support',
    independence: 'high',
    confidentiality: 'high',
    timing: 'Reactive (when distressed)',
    bestFor: 'Individual wellbeing counselling',
    isRecommended: false
  }, {
    name: 'Formal Investigation',
    purpose: 'Fact-finding for policy breach',
    independence: 'medium',
    confidentiality: 'low',
    timing: 'Late stage (after complaint)',
    bestFor: 'Determining if policy was breached',
    isRecommended: false
  }, {
    name: 'EquipMe External Ombuds',
    purpose: 'Independent conflict resolution & systemic insight',
    independence: 'high',
    confidentiality: 'high',
    timing: 'Early intervention (before complaints)',
    bestFor: 'Resolving issues early & building capability',
    isRecommended: true
  }];
  const getIndicator = (level: string) => {
    if (level === 'high') return <Check size={20} className="text-brand-teal" data-id="element-46" />;
    if (level === 'medium') return <Minus size={20} className="text-brand-orange" data-id="element-47" />;
    if (level === 'low') return <X size={20} className="text-brand-charcoal/30" data-id="element-48" />;
    return null;
  };
  return <section className="py-24 bg-gradient-to-br from-brand-off-white to-white" data-id="element-49">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-50">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mb-12" data-id="element-51">
          <h2 className="text-4xl md:text-5xl font-serif text-brand-charcoal mb-6 leading-tight" data-id="element-52">
            Not HR. Not an EAP. Not a formal investigation.
          </h2>
          <p className="text-lg text-brand-charcoal/80 leading-relaxed font-normal mb-4" data-id="element-53">
            Internal teams are essential, but some issues need a neutral
            pathway. EquipMe works alongside existing structures to create a
            safe, independent space for early resolution, insight and
            capability-building.
          </p>
          <p className="text-lg text-brand-charcoal/90 leading-relaxed font-medium" data-id="element-54">
            The question isn't whether you need HR, an EAP, or an investigation.
            It's whether you also need an independent pathway for the issues
            that don't fit neatly into those boxes.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-55">
          {options.map((option, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className={`rounded-sm p-8 transition-all ${option.isRecommended ? 'bg-white border-2 border-brand-teal shadow-xl shadow-brand-teal/20 relative' : 'bg-gray-50 border border-gray-200'}`} data-id="element-56">
              {option.isRecommended && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-teal text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider" data-id="element-57">
                  Recommended
                </div>}

              <h3 className={`text-lg font-serif mb-6 ${option.isRecommended ? 'text-brand-charcoal' : 'text-brand-charcoal/60'}`} data-id="element-58">
                {option.name}
              </h3>

              <div className="space-y-4 text-[15px]" data-id="element-59">
                <div data-id="element-60">
                  <p className="text-xs text-brand-charcoal/50 uppercase tracking-wider mb-1 font-semibold" data-id="element-61">
                    Purpose
                  </p>
                  <p className={option.isRecommended ? 'text-brand-charcoal font-medium' : 'text-brand-charcoal/70 font-normal'} data-id="element-62">
                    {option.purpose}
                  </p>
                </div>

                <div data-id="element-63">
                  <p className="text-xs text-brand-charcoal/50 uppercase tracking-wider mb-2 font-semibold" data-id="element-64">
                    Independence
                  </p>
                  <div className="flex items-center gap-2" data-id="element-65">
                    {getIndicator(option.independence)}
                    <span className={`text-sm font-medium ${option.isRecommended ? 'text-brand-charcoal' : 'text-brand-charcoal/70'}`} data-id="element-66">
                      {option.independence}
                    </span>
                  </div>
                </div>

                <div data-id="element-67">
                  <p className="text-xs text-brand-charcoal/50 uppercase tracking-wider mb-2 font-semibold" data-id="element-68">
                    Confidentiality
                  </p>
                  <div className="flex items-center gap-2" data-id="element-69">
                    {getIndicator(option.confidentiality)}
                    <span className={`text-sm font-medium ${option.isRecommended ? 'text-brand-charcoal' : 'text-brand-charcoal/70'}`} data-id="element-70">
                      {option.confidentiality}
                    </span>
                  </div>
                </div>

                <div data-id="element-71">
                  <p className="text-xs text-brand-charcoal/50 uppercase tracking-wider mb-1 font-semibold" data-id="element-72">
                    Timing
                  </p>
                  <p className={`text-sm ${option.isRecommended ? 'text-brand-charcoal font-medium' : 'text-brand-charcoal/70 font-normal'}`} data-id="element-73">
                    {option.timing}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-200" data-id="element-74">
                  <p className="text-xs text-brand-charcoal/50 uppercase tracking-wider mb-1 font-semibold" data-id="element-75">
                    Best for
                  </p>
                  <p className={`text-sm ${option.isRecommended ? 'text-brand-charcoal font-semibold' : 'text-brand-charcoal/70 font-normal'}`} data-id="element-76">
                    {option.bestFor}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}